import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useUpdateInvestmentDatesMutation,
  useGetAdminInvestmentQuery,
  useListInvestmentPaymentsQuery,
  useRecordPaymentMutation,
  useListInvestmentPayoutsQuery,
  useRecordPayoutMutation,
  useCloseInvestmentMutation,
  useRequestRolloverMutation,
  useCancelRolloverMutation,
  useListInvestmentRolloversQuery,
} from '../../features/investments/adminInvestmentsApi';
import { usePlanLookup } from '../../components/investments/usePlanLookup';
import InvestmentStatusBadge from '../../components/investments/InvestmentStatusBadge';
import FundingProgressBar from '../../components/investments/FundingProgressBar';
import PayoutInterface from '../../components/investments/PayoutInterface';
import PayoutTimeline from '../../components/investments/PayoutTimeline';
import RolloverStatusCard from '../../components/investments/RolloverStatusCard';
import RolloverHistory from '../../components/investments/RolloverHistory';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Dropdown from '../../components/ui/Dropdown';
import { DataRow } from '../../components/ui/MobileCard';
import { SkeletonDetailCard, SkeletonTable } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';
import { formatCurrency, formatDuration, formatRoi, formatMinorCurrency } from '../../lib/formatPlan';
import { buildRecordPaymentSchema, PAYMENT_METHOD_SUGGESTIONS } from '../../lib/validators/recordPaymentSchema';
import { buildRecordPayoutSchema, PAYOUT_TYPE_OPTIONS } from '../../lib/validators/recordPayoutSchema';
import { closeInvestmentSchema } from '../../lib/validators/closeInvestmentSchema';
import { rollOverSchema, cancelRolloverSchema } from '../../lib/validators/rolloverSchema';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

// paid_at is a date, not a datetime — a separate formatter avoids showing a
// misleading 00:00:00 time component that was never actually in the data.
function formatDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function RecordPaymentForm({ investment, onDone }) {
  const toast = useToast();
  const [recordPayment, { isLoading }] = useRecordPaymentMutation();
  const schema = buildRecordPaymentSchema(investment.remainingAmountMinor / 100);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { amount: '', method: 'bank_transfer', provider: '', provider_reference: '', note: '' } });

  const onSubmit = async (values) => {
    try {
      await recordPayment({
        investmentId: investment.id,
        amount: Number(values.amount).toFixed(2),
        method: values.method,
        ...(values.provider ? { provider: values.provider } : {}),
        ...(values.provider_reference ? { provider_reference: values.provider_reference } : {}),
        ...(values.note ? { note: values.note } : {}),
      }).unwrap();

      // Heuristic: the payment response doesn't include the investment's
      // new status, but we know its remaining balance before this call —
      // if this payment covers it, it just auto-activated server-side.
      const paidMinor = Math.round(Number(values.amount) * 100);
      const fullyFunded = paidMinor >= investment.remainingAmountMinor;
      toast.push({
        tone: 'success',
        message: fullyFunded ? 'Investment fully funded and now active.' : 'Payment recorded.',
      });
      onDone();
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
        return;
      }
      if (err?.status === 409) {
        setError('root', { message: 'This investment is no longer pending funding.' });
        return;
      }
      setError('root', { message: authErrorMessage(err, { fallback: 'Could not record this payment.' }) });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <p className="text-sm text-foreground-muted">
        Remaining to fund: <span className="text-foreground font-medium">{formatCurrency(investment.remainingAmount)}</span>
      </p>

      <Input label="Amount (₦)" type="number" step="0.01" error={errors.amount?.message} {...register('amount')} />

      <div>
        <Input
          label="Method"
          list="payment-method-suggestions"
          placeholder="bank_transfer"
          error={errors.method?.message}
          {...register('method')}
        />
        <datalist id="payment-method-suggestions">
          {PAYMENT_METHOD_SUGGESTIONS.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
      </div>

      <Input label="Provider (optional)" error={errors.provider?.message} {...register('provider')} />
      <Input label="Provider reference (optional)" error={errors.provider_reference?.message} {...register('provider_reference')} />
      <Input label="Note (optional)" error={errors.note?.message} {...register('note')} />

      {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

      <Button type="submit" className="w-full" isLoading={isLoading}>
        Record payment
      </Button>
    </form>
  );
}

/**
 * POST /admin/investments/{investment}/payouts — v1.16.0 breaking body
 * shape: { type: 'principal'|'interest', amount, paid_at, note } instead of
 * a combined { principal, interest } pair. `type` is always a manual,
 * explicit choice — never inferred from the entered amount, even when the
 * amount happens to match the current period's expected installment. That
 * rule is absolute: the backend genuinely cannot categorize an amount
 * without being told, which is the whole reason `type` exists as a
 * required field. Defaults to 'interest' (the common case, saves a click)
 * — a UX default, not a detection.
 *
 * `remainingPrincipal` is investment.principal minus investment.principalPaid
 * (backend ledger fields, not a client-side sum of payout rows) — only used
 * to bound the principal case; interest is deliberately uncapped.
 *
 * The live "after this payout" preview reads the current PayoutSummary
 * (`summary`, from the same query already powering the page's Payout
 * Interface section) and subtracts the typed amount — a pure display aid,
 * not a new validation constraint.
 */
function RecordPayoutForm({ investment, summary, remainingPrincipal, onDone }) {
  const toast = useToast();
  const [recordPayout, { isLoading }] = useRecordPayoutMutation();
  const schema = buildRecordPayoutSchema(remainingPrincipal);
  const today = new Date().toISOString().slice(0, 10);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { type: 'interest', amount: '', paid_at: today, note: '' } });

  const type = watch('type');
  const amountNum = Number(watch('amount')) || 0;
  const amountMinor = Math.round(amountNum * 100);
  const outstandingInterestMinor = summary?.outstandingInterestMinor ?? 0;
  const remainingPrincipalMinor = remainingPrincipal != null ? Math.round(remainingPrincipal * 100) : 0;
  const previewInterestMinor = type === 'interest' ? Math.max(0, outstandingInterestMinor - amountMinor) : outstandingInterestMinor;
  const previewPrincipalMinor = type === 'principal' ? Math.max(0, remainingPrincipalMinor - amountMinor) : remainingPrincipalMinor;

  const onSubmit = async (values) => {
    try {
      await recordPayout({
        investmentId: investment.id,
        type: values.type,
        amount: Number(values.amount).toFixed(2),
        paid_at: values.paid_at,
        ...(values.note ? { note: values.note } : {}),
      }).unwrap();
      toast.push({ tone: 'success', message: `${values.type === 'principal' ? 'Principal' : 'Interest'} payout recorded.` });
      onDone();
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
        return;
      }
      setError('root', { message: authErrorMessage(err, { fallback: 'Could not record this payout.' }) });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <Dropdown
        label="Type"
        options={PAYOUT_TYPE_OPTIONS}
        value={type}
        onChange={(v) => setValue('type', v, { shouldValidate: true })}
        error={errors.type?.message}
      />

      {type === 'principal' && remainingPrincipal != null && (
        <p className="text-sm text-foreground-muted">
          Outstanding principal: <span className="text-foreground font-medium">{formatCurrency(remainingPrincipal)}</span>
        </p>
      )}

      <Input
        label={type === 'principal' ? 'Principal amount (₦)' : 'Interest amount (₦)'}
        type="number"
        step="0.01"
        error={errors.amount?.message}
        {...register('amount')}
      />

      <Input label="Date paid" type="date" max={today} error={errors.paid_at?.message} {...register('paid_at')} />

      <div>
        <label className="block text-sm font-medium text-foreground-muted mb-1.5">Note (optional)</label>
        <textarea
          rows={3}
          placeholder="Context for this payout"
          className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong"
          {...register('note')}
        />
        {errors.note && <p className="text-xs text-danger mt-1">{errors.note.message}</p>}
      </div>

      {amountNum > 0 && summary && (
        <div className="rounded-xl border border-border bg-background-elevated p-3 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">After this payout</p>
          <p className="text-sm text-foreground-muted">
            Outstanding interest will be: <span className="text-foreground font-medium">{formatMinorCurrency(previewInterestMinor)}</span>
          </p>
          {remainingPrincipal != null && (
            <p className="text-sm text-foreground-muted">
              Outstanding principal will be: <span className="text-foreground font-medium">{formatMinorCurrency(previewPrincipalMinor)}</span>
            </p>
          )}
        </div>
      )}

      {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

      <Button type="submit" className="w-full" isLoading={isLoading}>
        Record payout
      </Button>
    </form>
  );
}

/**
 * POST /admin/investments/{investment}/close — ends the investment's
 * lifecycle. The docs don't enumerate which statuses are closeable, so
 * this doesn't gate beyond the obvious (see the `canClose` check below,
 * which just excludes pending_funding) — a 409 for any other non-closeable
 * status is caught and surfaced clearly rather than guessed at up front.
 */
function CloseInvestmentForm({ investment, onDone }) {
  const toast = useToast();
  const [closeInvestment, { isLoading }] = useCloseInvestmentMutation();
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm({ resolver: zodResolver(closeInvestmentSchema), defaultValues: { reason: '' } });
  const reasonLength = (watch('reason') || '').length;

  const onSubmit = async (values) => {
    try {
      await closeInvestment({ investmentId: investment.id, reason: values.reason?.trim() || undefined }).unwrap();
      toast.push({ tone: 'success', message: 'Investment closed.' });
      onDone();
    } catch (err) {
      if (err?.status === 409) {
        setError('root', { message: "This investment can't be closed from its current status." });
        return;
      }
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
        return;
      }
      setError('root', { message: authErrorMessage(err, { fallback: 'Could not close this investment.' }) });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <p className="text-sm text-foreground-muted">This ends the investment's lifecycle and can't be undone from here.</p>

      <div>
        <label className="block text-sm font-medium text-foreground-muted mb-1.5">Reason (optional)</label>
        <textarea
          rows={3}
          maxLength={500}
          placeholder="Why this investment is being closed"
          className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong"
          {...register('reason')}
        />
        <div className="flex items-center justify-between mt-1">
          {errors.reason ? <p className="text-xs text-danger">{errors.reason.message}</p> : <span />}
          <p className="text-xs text-foreground-subtle">{reasonLength}/500</p>
        </div>
      </div>

      {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

      <Button type="submit" variant="danger" className="w-full" isLoading={isLoading}>
        Close investment
      </Button>
    </form>
  );
}

/**
 * POST /admin/investments/{investment}/rollover — v1.17.0. `note` is
 * optional. The response's `rollover` block tells us whether this executed
 * immediately (current term's interest was already fully paid) or was
 * merely scheduled (will auto-execute on the final interest payout or the
 * nightly sweep) — the two outcomes get distinct success copy so an admin
 * never mistakes "queued" for "done". A 422 here means the backend judged
 * the investment ineligible at submit time; its message is written to
 * explain why and is surfaced inline rather than replaced with a generic
 * toast.
 */
function RollOverForm({ investment, onDone }) {
  const toast = useToast();
  const [requestRollover, { isLoading }] = useRequestRolloverMutation();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({ resolver: zodResolver(rollOverSchema), defaultValues: { note: '' } });

  const onSubmit = async (values) => {
    try {
      const result = await requestRollover({ investmentId: investment.id, note: values.note?.trim() || undefined }).unwrap();
      const executed = !result?.rollover?.scheduledRollover;
      toast.push({
        tone: 'success',
        message: executed
          ? 'Term extended — rollover executed.'
          : 'Rollover scheduled — will execute once the final interest for this term is paid.',
      });
      onDone();
    } catch (err) {
      if (err?.status === 422) {
        setError('root', { message: authErrorMessage(err, { fallback: 'This investment is not eligible for rollover.' }) });
        return;
      }
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
        return;
      }
      setError('root', { message: authErrorMessage(err, { fallback: 'Could not roll over this investment.' }) });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <p className="text-sm text-foreground-muted">
        This extends the current term by {formatDuration(investment.rolloverTermDays)} instead of closing the investment. If
        this term's interest isn't fully paid yet, the rollover will be scheduled to execute automatically once it is.
      </p>

      <div>
        <label className="block text-sm font-medium text-foreground-muted mb-1.5">Note (optional)</label>
        <textarea
          rows={3}
          placeholder="Context for this rollover"
          className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong"
          {...register('note')}
        />
        {errors.note && <p className="text-xs text-danger mt-1">{errors.note.message}</p>}
      </div>

      {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

      <Button type="submit" className="w-full" isLoading={isLoading}>
        Roll over
      </Button>
    </form>
  );
}

/**
 * DELETE /admin/investments/{investment}/rollover — v1.17.0. Cancels a
 * scheduled (not-yet-executed) rollover. `reason` is required — cancelling
 * something the investor was told would happen automatically is the one
 * action in this domain where an explanation isn't optional.
 */
function CancelRolloverForm({ investment, onDone }) {
  const toast = useToast();
  const [cancelRollover, { isLoading }] = useCancelRolloverMutation();
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm({ resolver: zodResolver(cancelRolloverSchema), defaultValues: { reason: '' } });
  const reasonLength = (watch('reason') || '').length;

  const onSubmit = async (values) => {
    try {
      await cancelRollover({ investmentId: investment.id, reason: values.reason.trim() }).unwrap();
      toast.push({ tone: 'success', message: 'Scheduled rollover cancelled.' });
      onDone();
    } catch (err) {
      if (err?.status === 422) {
        setError('root', { message: authErrorMessage(err, { fallback: 'Could not cancel this rollover.' }) });
        return;
      }
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
        return;
      }
      setError('root', { message: authErrorMessage(err, { fallback: 'Could not cancel this rollover.' }) });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <p className="text-sm text-foreground-muted">
        This cancels the scheduled rollover — the investment will follow its original maturity date instead.
      </p>

      <div>
        <label className="block text-sm font-medium text-foreground-muted mb-1.5">Reason</label>
        <textarea
          rows={3}
          maxLength={500}
          placeholder="Why this scheduled rollover is being cancelled"
          className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong"
          {...register('reason')}
        />
        <div className="flex items-center justify-between mt-1">
          {errors.reason ? <p className="text-xs text-danger">{errors.reason.message}</p> : <span />}
          <p className="text-xs text-foreground-subtle">{reasonLength}/500</p>
        </div>
      </div>

      {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

      <Button type="submit" variant="danger" className="w-full" isLoading={isLoading}>
        Cancel scheduled rollover
      </Button>
    </form>
  );
}

/**
 * GET /admin/investments/{investment} — funding progress uses the _minor
 * integer fields for the percentage math (never the decimal strings), per
 * the spec's guidance on precision. Record Payment only renders while
 * status === 'pending_funding' — the server also 409s a payment on an
 * already-active investment, but hiding the action is the primary guard.
 *
 * v1.16.0: the outstanding principal balance used to bound/preview the
 * Record Payout form now comes from the investment's own ledger fields
 * (principalMinor - principalPaidMinor, both backend-provided) instead of
 * summing the payouts list client-side — the addendum explicitly forbids
 * the old approach, since the backend intentionally allows flexible/
 * out-of-order payout recording that a client-side sum can't represent
 * correctly at a glance.
 */
function EditDatesForm({ investment, onDone }) {
  const [updateDates, { isLoading }] = useUpdateInvestmentDatesMutation();
  const toast = useToast();
  const { register, handleSubmit, setError, watch, formState: { errors } } = useForm({
    defaultValues: { start_date: investment.startDate?.slice(0, 10) ?? '', maturity_date: investment.maturityDate?.slice(0, 10) ?? '' },
  });
  const onSubmit = async (values) => {
    try {
      await updateDates({ investmentId: investment.id, ...values }).unwrap();
      toast.push({ tone: 'success', message: 'Investment dates updated.' });
      onDone();
    } catch (err) {
      if (err?.data?.errors) {
        Object.entries(err.data.errors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
      } else {
        setError('root', { message: authErrorMessage(err, { fallback: 'Could not update investment dates.' }) });
      }
    }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <p className="text-sm text-foreground-muted">Changing dates updates the duration and payout schedule. The agreed total interest and recorded payouts stay the same. Pending investments still require full funding to activate.</p>
      <Input label="Actual start date" type="date" error={errors.start_date?.message}
        {...register('start_date', { required: 'Start date is required.' })} />
      <Input label="End date (maturity)" type="date" error={errors.maturity_date?.message}
        {...register('maturity_date', { required: 'End date is required.', validate: value => value > watch('start_date') || 'End date must be after the start date.' })} />
      {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      <Button type="submit" className="w-full" isLoading={isLoading}>Save dates</Button>
    </form>
  );
}

export default function InvestmentDetail() {
  const { id } = useParams();
  const { data: investment, isLoading, isError, error } = useGetAdminInvestmentQuery(id, { skip: !id });
  const { data: paymentsData, isLoading: isLoadingPayments } = useListInvestmentPaymentsQuery(
    { investmentId: id },
    { skip: !id }
  );
  const { data: payoutsData, isLoading: isLoadingPayouts } = useListInvestmentPayoutsQuery(
    { investmentId: id },
    { skip: !id }
  );
  const { data: rolloversData, isLoading: isLoadingRollovers } = useListInvestmentRolloversQuery(
    { investmentId: id },
    { skip: !id }
  );
  const getPlan = usePlanLookup();
  const [datesOpen, setDatesOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [rollOverOpen, setRollOverOpen] = useState(false);
  const [cancelRolloverOpen, setCancelRolloverOpen] = useState(false);

  const payments = paymentsData?.payments ?? [];
  const payouts = payoutsData?.payouts ?? [];
  const rollovers = rolloversData?.rollovers ?? [];
  const summary = payoutsData?.summary ?? investment?.payoutSummary ?? null;
  const plan = investment ? getPlan(investment.investmentPlanUuid) : null;
  const canEditDates = investment && ['pending_funding', 'active', 'matured'].includes(investment.status)
    && !investment.closedAt && !investment.isPaidOut && !investment.rolloverCount
    && !investment.rollover?.scheduledRollover;
  const isPendingFunding = investment?.status === 'pending_funding';
  // Not tied to any documented status enum beyond the obvious — an
  // investment with nothing funded yet has nothing to close. Anything else
  // is allowed to try; a genuinely non-closeable status still 409s and
  // that's surfaced clearly in CloseInvestmentForm rather than guessed at
  // here.
  const canClose = investment && investment.status !== 'pending_funding';
  // Backend ledger fields — never a client-side sum of payout rows.
  const remainingPayoutPrincipalMinor = investment ? Math.max(0, investment.principalMinor - investment.principalPaidMinor) : 0;

  return (
    <div className="space-y-6">
      <Link to="/admin/investments" className="text-sm text-accent hover:underline">
        ← Back to investments
      </Link>

      {isLoading && <SkeletonDetailCard fields={7} />}

      {isError && (
        <Card>
          <p className="py-6 text-center text-danger">{authErrorMessage(error, { notFound: 'Investment not found.' })}</p>
        </Card>
      )}

      {investment && (
        <>
          {investment.closedAt && (
            <div className="rounded-xl border border-border-strong bg-background-elevated px-4 py-3">
              <p className="text-sm font-medium text-foreground">
                Closed on {formatDateOnly(investment.closedAt)}
                {investment.closedBy?.fullName ? ` by ${investment.closedBy.fullName}` : ''}
                {investment.closeReason ? ` — ${investment.closeReason}` : ''}
              </p>
            </div>
          )}

          <Card className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-lg font-semibold text-foreground">{plan?.name || 'Unknown plan'}</h1>
                <p className="text-sm text-foreground-muted">Created {formatDate(investment.createdAt)}</p>
              </div>
              <div className="flex gap-2">
                {investment.isOverride && <Badge tone="warning">Overridden</Badge>}
                <InvestmentStatusBadge status={investment.status} />
              </div>
            </div>

            <FundingProgressBar fundedMinor={investment.fundedAmountMinor} principalMinor={investment.principalMinor} />

            <div className="grid grid-cols-2 gap-3 text-sm">
              <DataRow label="Principal" value={formatCurrency(investment.principal)} />
              <DataRow label="ROI" value={formatRoi(investment.roiPercentage)} />
              <DataRow label="Duration" value={formatDuration(investment.durationInDays)} />
              <DataRow label="Funding method" value={investment.fundingMethod || '—'} />
              <DataRow label="Funded" value={formatCurrency(investment.fundedAmount)} />
              <DataRow label="Remaining" value={formatCurrency(investment.remainingAmount)} />
              <DataRow label="Payout frequency" value={investment.payoutSummary?.frequencyLabel || '—'} />
              <DataRow label="Next payout due" value={investment.nextPayoutDueAt ? formatDateOnly(investment.nextPayoutDueAt) : '—'} />
              <DataRow label="Start date" value={investment.startDate ? formatDate(investment.startDate) : 'Not yet active'} />
              <DataRow label="Maturity date" value={investment.maturityDate ? formatDate(investment.maturityDate) : 'Not yet active'} />
              <DataRow label="Offline status" value={investment.offlineStatus ? investment.offlineStatus.replace(/_/g, ' ') : '—'} />
              {investment.rolloverTermDays > 0 && (
                <DataRow label="Rollover term" value={formatDuration(investment.rolloverTermDays)} />
              )}
              {investment.rolloverCount > 0 && (
                <>
                  <DataRow label="Current term started" value={investment.currentTermStartedAt ? formatDate(investment.currentTermStartedAt) : '—'} />
                  <DataRow
                    label={`Interest (current term)`}
                    value={formatCurrency(investment.currentTermInterest)}
                  />
                  <DataRow
                    label={`Interest (lifetime, across ${investment.rolloverCount + 1} terms)`}
                    value={formatCurrency(investment.totalInterest)}
                  />
                </>
              )}
            </div>

            {investment.isOverride && (
              <div className="rounded-xl border border-warning/30 bg-warning-soft p-3 text-sm text-warning">
                This investment overrides the plan defaults
                {investment.customRoiPercentage != null && ` (ROI: ${formatRoi(investment.customRoiPercentage)})`}
                {investment.customDurationInDays != null && ` (Duration: ${investment.customDurationInDays} days)`}.
              </div>
            )}

            {(isPendingFunding || canClose || canEditDates) && (
              <div className="pt-3 border-t border-border flex flex-wrap gap-2">
                {canEditDates && <Button size="sm" onClick={() => setDatesOpen(true)}>Edit dates</Button>}
                {isPendingFunding && (
                  <Button size="sm" onClick={() => setRecordOpen(true)}>
                    Record payment
                  </Button>
                )}
                {canClose && (
                  <Button size="sm" variant="danger" onClick={() => setCloseOpen(true)}>
                    Close investment
                  </Button>
                )}
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title="Payments" />
            {isLoadingPayments && <SkeletonTable columns={['Amount', 'Method', 'Reference', 'Status', 'Date']} rows={3} />}
            {!isLoadingPayments && payments.length === 0 && (
              <p className="py-6 text-center text-foreground-muted">No payments recorded yet.</p>
            )}
            {!isLoadingPayments && payments.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-foreground-subtle border-b border-border">
                      <th className="py-2 pr-4 font-medium">Amount</th>
                      <th className="py-2 pr-4 font-medium">Method</th>
                      <th className="py-2 pr-4 font-medium">Reference</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td className="py-3 pr-4 text-foreground">{formatCurrency(p.amount)}</td>
                        <td className="py-3 pr-4 text-foreground-muted">{p.method || '—'}</td>
                        <td className="py-3 pr-4 text-foreground-muted">{p.providerReference || p.provider || '—'}</td>
                        <td className="py-3 pr-4">
                          <Badge tone={p.status === 'success' ? 'success' : 'neutral'}>{(p.status || '—').toUpperCase()}</Badge>
                        </td>
                        <td className="py-3 text-foreground-muted">{formatDate(p.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <PayoutInterface summary={summary} onRecordPayout={() => setPayoutOpen(true)} />

          <Card>
            <CardHeader title="Payout history" />
            {isLoadingPayouts && <SkeletonTable columns={['Date', 'Type', 'Amount', 'Note']} rows={3} />}
            {!isLoadingPayouts && <PayoutTimeline payouts={payouts} />}
          </Card>

          {investment.rollover && (
            <RolloverStatusCard
              rollover={investment.rollover}
              onRollover={() => setRollOverOpen(true)}
              onCancelRollover={() => setCancelRolloverOpen(true)}
            />
          )}

          {investment.rollover && (
            <Card>
              <CardHeader title="Rollover history" />
              {isLoadingRollovers && <SkeletonTable columns={['Term', 'State', 'Date']} rows={2} />}
              {!isLoadingRollovers && <RolloverHistory rollovers={rollovers} />}
            </Card>
          )}
        </>
      )}

      <Modal open={datesOpen} onClose={() => setDatesOpen(false)} title="Edit investment dates">
        {investment && <EditDatesForm investment={investment} onDone={() => setDatesOpen(false)} />}
      </Modal>
      <Modal open={recordOpen} onClose={() => setRecordOpen(false)} title="Record payment">
        {investment && <RecordPaymentForm investment={investment} onDone={() => setRecordOpen(false)} />}
      </Modal>

      <Modal open={payoutOpen} onClose={() => setPayoutOpen(false)} title="Record payout">
        {investment && (
          <RecordPayoutForm
            investment={investment}
            summary={summary}
            remainingPrincipal={remainingPayoutPrincipalMinor / 100}
            onDone={() => setPayoutOpen(false)}
          />
        )}
      </Modal>

      <Modal open={closeOpen} onClose={() => setCloseOpen(false)} title="Close investment">
        {investment && <CloseInvestmentForm investment={investment} onDone={() => setCloseOpen(false)} />}
      </Modal>

      <Modal open={rollOverOpen} onClose={() => setRollOverOpen(false)} title="Roll over investment">
        {investment && <RollOverForm investment={investment} onDone={() => setRollOverOpen(false)} />}
      </Modal>

      <Modal open={cancelRolloverOpen} onClose={() => setCancelRolloverOpen(false)} title="Cancel scheduled rollover">
        {investment && <CancelRolloverForm investment={investment} onDone={() => setCancelRolloverOpen(false)} />}
      </Modal>
    </div>
  );
}
